// Policy objects — all eleven kinds through one route family (`06` §3.8,
// `SC-F500`–`SC-F533`).
//
// **Structured records are the source of truth; YAML is rendered, read-only and
// exportable.** There is no raw-YAML editing field anywhere in the product
// (`03` §1.1, §3.13), which is why [getPolicyYaml] returns a string and there is
// no `putPolicyYaml`.
//
// Objects are addressed by `(kind, name)` — exactly Sluice's own uniqueness key
// across the whole policy directory. `apiVersion` is **derived from `kind`** and
// is never client input: a mismatch is a decode error upstream, so the Cloud
// never offers the choice (`SC-F502`).
//
// The type of a `spec` is deliberately `Record<string, unknown>` here. Frozen
// interface `I3` makes the hand-maintained **form schema** the contract the UI
// codes against — not a Go struct and not a TypeScript mirror of one — because
// the schema is the only artifact that carries per-field requiredness, the
// closed enums, and the status vocabulary (`enforced`, `declared-only`,
// `broken`, `rejected-at-decode`, `rejected-at-compile`, `blocked-by-us`) that
// decides whether a field is offered, hidden or badged. A hand-mirrored spec
// type would drift from it silently and re-offer fields the pinned build does
// not read.

import {
  del,
  getJSON,
  getWithETag,
  newIdempotencyKey,
  query,
  sendJSON,
} from "./http.js";

/**
 * PolicyKind is the closed set of eleven kinds. `DataSource` is listed here
 * because it *is* one of the eleven and appears in listings and diffs, but it
 * has its own route family in `api/datasources.ts` — its editor is a driver
 * form, not a selector form.
 */
export type PolicyKind =
  | "DataSource"
  | "SubjectBinding"
  | "DataClassification"
  | "SqlAccessPolicy"
  | "RowFilterPolicy"
  | "ColumnMaskPolicy"
  | "QueryRejectPolicy"
  | "QueryRewritePolicy"
  | "ApprovalPolicy"
  | "RelationshipPolicy"
  | "AuditSink";

/** POLICY_KINDS is the eleven in canonical order — catalogs and identities
 * first, enforcement after. It is the same order the renderer's fixed file set
 * uses (`00-datasource.yaml` … `95-auditsink.yaml`), so a list and a bundle read
 * alike. */
export const POLICY_KINDS: readonly PolicyKind[] = [
  "DataSource",
  "SubjectBinding",
  "DataClassification",
  "SqlAccessPolicy",
  "RowFilterPolicy",
  "ColumnMaskPolicy",
  "QueryRejectPolicy",
  "QueryRewritePolicy",
  "ApprovalPolicy",
  "RelationshipPolicy",
  "AuditSink",
];

/**
 * EnforcementMode is whether a matching policy acts or only records. Three
 * values, not four: `DryRun` is behaviourally identical to `Audit` upstream, and
 * the product says so rather than implying a third behaviour.
 *
 * This is the one field `PATCH` may change on its own, because the shadow
 * rollout (`SC-F527`) is exactly a mode flip.
 */
export type EnforcementMode = "Enforce" | "Audit" | "DryRun";

/** FindingSeverity is the linter's severity ladder (`06` §4.6). A
 * `blocking-warning` is the interesting rung: it does not stop Sluice, and the
 * Cloud's publish gate stops it anyway. */
export type FindingSeverity = "error" | "blocking-warning" | "warning" | "info" | "hint";

/**
 * PolicyFinding is the one finding shape the whole product uses: the
 * control-plane linter (`SC-F529`) on every save, the proxied `policy validate`
 * (`SC-F702`), the publish gate, and `details.findings` on a `policy_invalid`
 * error. One shape, one findings table, everywhere.
 */
export interface PolicyFinding {
  severity: FindingSeverity;
  /** Stable rule id, e.g. `L01`, `mask.post-query-context`. */
  rule: string;
  kind?: PolicyKind;
  name?: string;
  /** The object's own rendered path — `spec.match.any[0].resources.tables[1]` —
   * because that is the one address the form, the YAML view and the diff already
   * agree on. Empty means the finding is about the object as a whole. */
  path?: string;
  /** The finding in context: the runtime consequence with the offending value
   * quoted into it. */
  message: string;
  /** The rule's own headline, carried so a client renders one findings table
   * without shipping a copy of the rule registry. Absent for a validator's own
   * finding, which has no registry entry. */
  title?: string;
  /** Why the shape is a defect rather than a style preference. */
  explanation?: string;
  /** The Sluice source line that proves it, at the pinned commit. */
  upstream?: string;
  /** True for a rule that can only be decided against the instance a generation
   * is published to. The UI says "checked at publish" rather than implying a
   * save-time all-clear it did not earn. */
  checked_at_publish?: boolean;
}

/** blocksSave mirrors `policymodel.Severity.BlocksSave`: only `error` refuses
 * the write. The split is `03` §7.1's and is never re-decided client-side —
 * a client that guessed would disagree with the API about whether an edit can
 * be stored. */
export function blocksSave(f: PolicyFinding): boolean {
  return f.severity === "error";
}

/** blocksPublish mirrors `policymodel.Severity.BlocksPublish`. Every `error`
 * blocks the publish too: a shape that cannot be saved cannot be shipped. */
export function blocksPublish(f: PolicyFinding): boolean {
  return f.severity === "error" || f.severity === "blocking-warning";
}

/** ObjectMeta is the identity block every kind carries. `namespace`, `labels`
 * and `annotations` decode and are read by nothing in Sluice; the editor shows
 * them inert rather than pretending they group anything. */
export interface ObjectMeta {
  name: string;
  priority?: number;
  namespace?: string | null;
  labels?: Record<string, string>;
  annotations?: Record<string, string>;
}

/**
 * PolicyDerived carries what the server computed about an object and the client
 * cannot. Every field answers a documented Sluice trap:
 *
 * - `mask_stage` — `hash` switches between the SQL stage and the post-query
 *   stage based on its `algorithm` argument, and a post-query masked column is
 *   refused anywhere except a bare top-level select item, returning
 *   `ERR_MASK_UNSUPPORTED_CONTEXT`. `SC-F516` requires the warning at authoring
 *   time.
 * - `specificity` — masks never stack. Candidates sort priority DESC →
 *   specificity DESC → name ASC and the first to claim a column wins. Without
 *   the score the UI cannot show who wins.
 * - `resolved_columns` — backed by the schema cache, with an explicit `[]` for
 *   "matches nothing": a `ColumnMaskPolicy` with neither `resources.columns` nor
 *   tags masks nothing at all, silently (`SC-F515`).
 */
export interface PolicyDerived extends Record<string, unknown> {
  api_version: string;
  mask_stage?: "sql" | "post_query";
  specificity?: number;
  resolved_columns?: string[];
  /** `fresh`, `stale` or `unavailable`, so a preview can carry the caveat
   * `SC-S507` requires instead of implying a live answer. */
  resolution_state?: "fresh" | "stale" | "unavailable";
  /** This object's 1-based place in the order Sluice evaluates its kind —
   * priority DESC, then name ASC — among the *enabled* objects of that kind.
   * It backs the ordering preview (`03` §3.2) and, for a `RowFilterPolicy`, the
   * fold order (`SC-F512`): a tie broken by NAME is invisible otherwise. */
  snapshot_position?: number;
  snapshot_total?: number;
  /** The EFFECTIVE mode, with Sluice's default applied. The stored spec omits
   * the field when it is absent and an absent mode means `Enforce`, so a client
   * reading the spec alone would show a blank where the strongest setting is in
   * force. */
  enforcement_mode?: EnforcementMode;
  /** True when the object is evaluated and changes no outcome — `Audit` and
   * `DryRun` alike, because the two are behaviourally identical in the pinned
   * build (`SC-F527`). */
  shadow?: boolean;
}

/**
 * PolicyObject is one structured record as the API returns it — `policies.View`.
 *
 * `id` is the **join row's** id, this object's identity within this instance's
 * generation, and deliberately not the content-addressed `config_objects` id:
 * that row changes on every edit and is shared between two instances configured
 * identically, so a client holding it across an edit would be looking at a
 * different record.
 */
export interface PolicyObject {
  id: string;
  kind: PolicyKind;
  metadata: ObjectMeta;
  /** Shape is owned by the form schema (`I3`), not by this type. Null when this
   * build cannot decode the stored spec — see [PolicyObject.unreadable]. */
  spec: Record<string, unknown> | null;
  derived: PolicyDerived;
  /** Always present — the linter speaking on every save, not only at publish. */
  findings: PolicyFinding[];
  /** False means the object is in the generation and out of the render: a soft
   * delete that keeps the content and does not change the digest. */
  enabled: boolean;
  /** The object's content identity, and this record's `ETag`. Send it back as
   * `ifMatch`: the draft is SHARED, one per instance, and every maintainer edits
   * the same object set. */
  digest: string;
  /** The form-schema version the document was authored at. */
  spec_version: number;
  updated: string;
  updated_by: string;
  /** Attribution snapshot taken at edit time, so a removed member's edits stay
   * legible. */
  updated_by_label: string;
  /** State relative to the generation the draft was forked from. */
  change?: "added" | "edited" | "removed" | "unchanged";
  /** Why this build cannot decode the stored spec. The record is still listed
   * and still deletable: a customer must be able to remove an object we cannot
   * parse. */
  unreadable?: string;
}

/**
 * PolicyRow is one **list** entry, and deliberately not a [PolicyObject]: a list
 * renders six columns and two badges, and shipping every object's full spec
 * would make it the largest response in the product for no reader's benefit.
 */
export interface PolicyRow {
  id: string;
  kind: PolicyKind;
  name: string;
  priority: number;
  /** The effective mode, so a row never shows a blank where `Enforce` is in
   * force. Absent on the four non-enforcement kinds. */
  enforcement_mode?: EnforcementMode;
  shadow?: boolean;
  enabled: boolean;
  findings: PolicyRowFindings;
  updated?: string;
  updated_by?: string;
  updated_by_label?: string;
  change?: string;
  digest?: string;
  unreadable?: string;
}

/** PolicyRowFindings is the per-row finding summary. `rules` carries the
 * distinct rule ids so a badge can link to the worst one without a second
 * request. */
export interface PolicyRowFindings {
  errors?: number;
  blocking?: number;
  warnings?: number;
  rules?: string[];
}

/**
 * PolicyNotice is a list-level statement with a machine-readable code. The code
 * is what makes it API rather than copy — a client branches on `default_deny` —
 * and `action` names the route that resolves it, so a CLI prints the same
 * guidance the console renders as a button.
 */
export interface PolicyNotice {
  /** `default_deny` (`SC-S509`), `empty_configuration`, or `shadow_only`. */
  code: string;
  severity: FindingSeverity;
  message: string;
  action?: string;
}

/**
 * PolicyList is `GET …/policies`. It is **not** the generic keyset `Page`: the
 * policy list carries two things a list of rows cannot, and both are load
 * bearing. `notices` is where "every query is denied by default" is said
 * (`SC-S509`), and `total` is the filtered size before pagination, so a client
 * can say "12 of 40" without walking every page. The cursor is an offset into
 * the snapshot-ordered set rather than a keyset tuple, because the sort key is
 * `(priority DESC, name ASC, kind)` over a set small enough to sort wholesale.
 */
export interface PolicyList {
  items: PolicyRow[];
  next: string | null;
  notices: PolicyNotice[];
  total: number;
}

/** WritePolicyInput is the create and replace body. `apiVersion` is absent by
 * design: it is derived from `kind`. */
export interface WritePolicyInput {
  kind: PolicyKind;
  metadata: ObjectMeta;
  spec: Record<string, unknown>;
}

const base = (instanceId: string) => `/v1/instances/${encodeURIComponent(instanceId)}/policies`;
const child = (instanceId: string, kind: PolicyKind, name: string) =>
  `${base(instanceId)}/${encodeURIComponent(kind)}/${encodeURIComponent(name)}`;

/**
 * listPolicies lists the draft's policy objects (`SC-F526`, `SC-S521`).
 *
 * Rows arrive in **the snapshot order Sluice will use** — priority descending,
 * then name ascending — not alphabetically, and a client must not re-sort them.
 * Every "first wins" rule downstream inherits that order: masks never stack and
 * the first candidate to claim a column wins, row filters fold in that order, and
 * a tie between two policies at the same priority is broken by NAME. A list
 * sorted by name would hide the tie-break that decides the outcome.
 */
export function listPolicies(
  instanceId: string,
  params: {
    kind?: PolicyKind;
    q?: string;
    enforcement?: EnforcementMode;
    cursor?: string;
    limit?: number;
  } = {},
): Promise<PolicyList> {
  return getJSON<PolicyList>(`${base(instanceId)}${query({ ...params })}`);
}

/** getPolicy reads one structured record. */
export function getPolicy(
  instanceId: string,
  kind: PolicyKind,
  name: string,
): Promise<PolicyObject> {
  return getJSON<PolicyObject>(child(instanceId, kind, name));
}

/** getPolicyWithETag is [getPolicy] plus the `ETag` an editor must send back as
 * `If-Match`. The tag is the object's content digest, so two maintainers who read
 * the same object read the same tag and the second writer's `If-Match` stops
 * matching the moment the first has landed. */
export function getPolicyWithETag(
  instanceId: string,
  kind: PolicyKind,
  name: string,
): Promise<{ value: PolicyObject; etag: string }> {
  return getWithETag<PolicyObject>(child(instanceId, kind, name));
}

/** createPolicy adds an object to the draft. */
export function createPolicy(
  instanceId: string,
  input: WritePolicyInput,
): Promise<PolicyObject> {
  return sendJSON<PolicyObject>(base(instanceId), "POST", input, {
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * replacePolicy replaces the **whole** record. There is no partial spec write:
 * policy specs are deeply nested discriminated unions, a partial merge into a
 * `RowFilterPolicy` predicate tree has no defensible semantics, and the form
 * holds the whole object anyway.
 *
 * Send the `ETag` from [getPolicy] as `ifMatch`. The server also strips every
 * mask argument that does not belong to the selected `type` before storing, so a
 * leftover argument from a type switch can never survive into the rendered YAML.
 */
export function replacePolicy(
  instanceId: string,
  kind: PolicyKind,
  name: string,
  input: WritePolicyInput,
  opts: { ifMatch?: string } = {},
): Promise<PolicyObject> {
  return sendJSON<PolicyObject>(child(instanceId, kind, name), "PUT", input, opts);
}

/** PolicyEnvelopePatch is the only partial write the API offers: priority,
 * enforcement mode, labels and the soft-delete flag (`SC-F527`). The
 * shadow-rollout toggle is exactly `enforcementMode`, which is why this exists at
 * all. */
export interface PolicyEnvelopePatch {
  priority?: number;
  enforcementMode?: EnforcementMode;
  labels?: Record<string, string>;
  /** The soft delete of `05` §3.11: in the generation, out of the render. It is
   * the honest alternative to deleting a policy while debugging it. */
  enabled?: boolean;
  /** Required to LEAVE a shadow mode. `SC-S522`'s third criterion is an explicit
   * confirm on promotion, and the server refuses the flip without this rather
   * than trusting a client to have shown the evidence. */
  acknowledge?: boolean;
  /** Recorded on the audit event for a mode change — the answer to "why was this
   * enforced on the 14th". Optional, because refusing a promotion for want of
   * prose would be theatre. */
  reason?: string;
}

/** patchPolicy updates the envelope only. Step-up gated when the patch carries
 * `enforcementMode`. */
export function patchPolicy(
  instanceId: string,
  kind: PolicyKind,
  name: string,
  patch: PolicyEnvelopePatch,
  opts: { verification?: string; ifMatch?: string } = {},
): Promise<PolicyObject> {
  return sendJSON<PolicyObject>(child(instanceId, kind, name), "PATCH", patch, opts);
}

/**
 * PolicyModeChange describes an enforcement-mode transition — what the promotion
 * dialog of `SC-S522` renders.
 *
 * `observation` is the field that keeps the zero-match warning honest:
 * `unavailable` means no query trail answered and the count is UNKNOWN, which is
 * not the same as zero. Rendering `unavailable` as zero would fire the
 * never-matched warning on every promotion for a reason that has nothing to do
 * with the policy.
 */
export interface PolicyModeChange {
  from: string;
  to: string;
  /** `activating` when the policy starts changing outcomes, `deactivating` when
   * it stops, `neutral` for a flip between two modes that behave identically —
   * which `Audit` → `DryRun` is, in this build. */
  direction: "activating" | "deactivating" | "neutral" | "";
  observation: "counted" | "unavailable" | "not_applicable";
  matched_count: number;
  window_seconds?: number;
  /** `SC-S522`'s fourth criterion: promoting a policy that matched nothing is
   * where most matcher mistakes surface, because a wrong pattern matches nothing
   * rather than matching the wrong thing. */
  never_matched?: boolean;
}

/**
 * PolicyShadowReport is `GET …/policies/{kind}/{name}/shadow` (`SC-S522`).
 *
 * `Observation` is capitalized because the Go field carries no JSON tag; it is the
 * wire spelling and this type mirrors it rather than papering over it.
 */
export interface PolicyShadowReport {
  kind: PolicyKind;
  name: string;
  mode: EnforcementMode | "";
  shadow: boolean;
  Observation: PolicyModeChange;
}

/** getShadowStatus reads one policy's shadow observation without changing
 * anything, so the promotion dialog can be rendered **before** the
 * acknowledgement is sent. */
export function getShadowStatus(
  instanceId: string,
  kind: PolicyKind,
  name: string,
): Promise<PolicyShadowReport> {
  return getJSON<PolicyShadowReport>(`${child(instanceId, kind, name)}/shadow`);
}

/** deletePolicy removes the object from the draft. */
export function deletePolicy(
  instanceId: string,
  kind: PolicyKind,
  name: string,
): Promise<void> {
  return del(child(instanceId, kind, name));
}

/** getPolicyYaml renders one object as its manifest, **read-only** (`SC-F602`).
 * Feed it to `bn-code` with `language="yaml"`; never to an editable field. */
export async function getPolicyYaml(
  instanceId: string,
  kind: PolicyKind,
  name: string,
): Promise<string> {
  const { yaml } = await getJSON<{ yaml: string }>(`${child(instanceId, kind, name)}/yaml`);
  return yaml;
}

/** CopyPolicyInput copies one object from another instance's draft or a named
 * generation into this instance's draft (`SC-F533`). The instance in the path is
 * the **target**; every field here describes the source. */
export interface CopyPolicyInput {
  /** The instance to copy from. The caller needs viewer on it and maintainer on
   * the target, and no access to the source answers the 403-versus-404 discipline
   * rather than leaking that it exists. */
  source_instance: string;
  /**
   * A generation number **as a string**, or `"draft"`. It is a string on the wire
   * because the field carries two kinds of value and a JSON number would make
   * `"draft"` the special case rather than one of two spellings.
   */
  source_generation?: string;
  kind: PolicyKind;
  name: string;
  /** Rename on arrival; omit to keep the source name. A collision without one is
   * refused — a copy never silently overwrites. */
  new_name?: string;
  /** Pull the cross-object closure too — the classifications and bindings the
   * object references. Without it an incomplete closure is refused rather than
   * landed broken, because a dangling tag rejects the target's whole snapshot. */
  include_referenced?: boolean;
}

/** StrippedField is one value the copy refused to carry across. Credentials never
 * travel: a copied API-key binding keeps its shape and loses its `hashRef`, because
 * the pepper and the key are per-instance by construction. */
export interface StrippedField {
  kind: PolicyKind;
  name: string;
  path: string;
  reason: string;
  /** What to do on the target to make the object whole, so the response is a work
   * list rather than a warning. */
  action?: string;
}

/**
 * MissingReference is one cross-object reference the target cannot satisfy.
 *
 * `blocking` separates the DSL's one hard cross-object reference from all the soft
 * ones. A **tag** is blocking because an unknown tag is a compile error that rejects
 * the target's entire snapshot — the target keeps serving its previous policy and an
 * unrelated later edit appears not to take effect. A **catalog** is not, because an
 * unknown catalog never matches and never errors: the copy is honest, it just
 * protects nothing until the datasource exists.
 */
export interface MissingReference {
  /** The `object_refs` kind — `tag`, `catalog`, `secret_ref`. */
  ref_kind: string;
  /** The reference as written, wildcards included. */
  value: string;
  /** Where in the copied object it appears. */
  path: string;
  /** The kind that would satisfy it on the target, so a client can offer to copy
   * that object too. */
  provides?: string;
  /** True when `include_referenced` could resolve it. A tag can be satisfied by
   * copying its `DataClassification`; a secret reference cannot be satisfied by
   * copying anything. */
  copyable: boolean;
  blocking: boolean;
}

/** CopyResult is what the copy landed. */
export interface CopyResult {
  /** Every object written, in **write order** — the closure first and the requested
   * object last. The order is not cosmetic: the linter runs over the post-write
   * draft on every object, so a mask copied before the classification defining its
   * tag would be refused by the very rule the closure exists to satisfy. */
  copied: PolicyObject[];
  stripped?: StrippedField[];
  /** Echoed so the provenance is in the response as well as on the object. */
  source_generation: string;
  /** Soft references the target does not satisfy and that did **not** refuse the
   * copy. Refusing would be wrong — a wildcard covering nothing today may cover
   * something tomorrow — and silence would be worse. */
  unresolved?: MissingReference[];
}

/** copyPolicy copies an object between instances (`SC-F533`). It is a
 * structured-model copy inside the control plane: no YAML, no parser, no untrusted
 * text. */
export function copyPolicy(instanceId: string, input: CopyPolicyInput): Promise<CopyResult> {
  return sendJSON<CopyResult>(`${base(instanceId)}/copy`, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
  });
}

/** ImportClass is `03` §1.7's classification of one submitted document. `verbatim`
 * is reported and **refused** by this build: there is no text-object store, so an
 * object the form model cannot represent is a named product gap rather than a blob
 * smuggled through. */
export type ImportClass = "modeled" | "modeled-with-loss" | "verbatim" | "blocked";

/** ImportLoss is one thing the import did not carry — a comment, a key order, a
 * `metadata.namespace` value, a mask argument belonging to another type. */
export interface ImportLoss {
  path?: string;
  reason: string;
}

/** ImportedObject is one document's classification, in stream order. */
export interface ImportedObject {
  /** 1-based position in the stream, so a report about a 40-document paste names
   * the one that failed. */
  document: number;
  kind?: PolicyKind;
  name?: string;
  class: ImportClass;
  /** Why a `verbatim` or `blocked` classification. */
  reason?: string;
  losses?: ImportLoss[];
  /** The YAML this object will render to, so the report shows the before-and-after
   * `03` §1.7 asks for without a second request. */
  rendered?: string;
  findings?: PolicyFinding[];
}

/**
 * ImportReport is the per-object table `03` §1.7 step 5 requires the operator to
 * acknowledge before anything is written.
 *
 * `input_digest` is step 2's baseline. The rendered digest **will** differ —
 * comments, key order and document grouping are not preserved — and reporting both
 * is how that stops being a surprise.
 */
export interface ImportReport {
  input_digest: string;
  documents: number;
  objects: ImportedObject[];
  /** Every finding across every object, so a client renders one table for the whole
   * import. */
  findings: PolicyFinding[];
  /** False means this is the pre-acknowledgement report and nothing was written. */
  committed: boolean;
  imported?: PolicyObject[];
}

/**
 * importPolicies parses a YAML document stream into structured records (`SC-F531`,
 * v1).
 *
 * **Two phases through one endpoint.** Without `acknowledge` the answer is the
 * report and nothing is written; with it, the objects the report classified as
 * importable are written. The report is the same document either way, which is what
 * stops the acknowledgement from being a checkbox over something nobody saw.
 *
 * This is an import path and not an editing path: the records it produces are edited
 * through the forms like any other, and **no byte of the submitted text is stored**.
 */
export function importPolicies(
  instanceId: string,
  yaml: string,
  opts: { acknowledge?: boolean } = {},
): Promise<ImportReport> {
  return sendJSON<ImportReport>(
    `${base(instanceId)}/import`,
    "POST",
    { yaml, acknowledge: opts.acknowledge === true },
    { idempotencyKey: newIdempotencyKey() },
  );
}

/**
 * lintDraft runs the control-plane linter over the whole draft (`SC-F529`).
 * Synchronous — the linter is pure control-plane code with no instance round
 * trip, which is exactly why it can run on every save.
 */
export function lintDraft(
  instanceId: string,
): Promise<{ findings: PolicyFinding[]; blocks_publish: boolean }> {
  return sendJSON(`/v1/instances/${encodeURIComponent(instanceId)}/lint`, "POST", {});
}

// ──────────────────────────────────────────── SubjectBinding API keys ──

/** InstanceApiKey is a key that authenticates a **subject** to the instance's
 * own data plane (`SC-F521`) — not a Sluice Cloud credential. The two never mix:
 * this one is minted against a `SubjectBinding` and is presented to the
 * instance, never to the control plane. */
export interface InstanceApiKey {
  id: string;
  /** Display prefix only; the plaintext is returned exactly once, at creation. */
  display_prefix: string;
  created: string;
  last_used: string;
  expires: string | null;
}

/** InstanceApiKeyCreated adds the one-time plaintext. */
export interface InstanceApiKeyCreated extends InstanceApiKey {
  secret: string;
}

/** mintSubjectApiKey mints an instance API key for a SubjectBinding
 * (`SC-F521`, v1). Step-up gated; the plaintext appears exactly once. */
export function mintSubjectApiKey(
  instanceId: string,
  bindingName: string,
  input: { expires_in_seconds: number | null },
  opts: { verification?: string } = {},
): Promise<InstanceApiKeyCreated> {
  return sendJSON<InstanceApiKeyCreated>(
    `${child(instanceId, "SubjectBinding", bindingName)}/api-keys`,
    "POST",
    input,
    { idempotencyKey: newIdempotencyKey(), verification: opts.verification },
  );
}

/** revokeSubjectApiKey revokes one instance API key (`SC-F521`). */
export function revokeSubjectApiKey(
  instanceId: string,
  bindingName: string,
  keyId: string,
): Promise<void> {
  return del(
    `${child(instanceId, "SubjectBinding", bindingName)}/api-keys/${encodeURIComponent(keyId)}`,
  );
}

// ────────────────────────────────────────────────────── form schema ──

/**
 * FormFieldStatus is the vocabulary that decides whether the editor offers a
 * field, hides it, or shows it disabled and badged. It is the mechanism behind
 * `concept.md` §7 principle 8: a field the pinned build does not read is never
 * presented as working.
 */
export type FormFieldStatus =
  | "enforced"
  | "declared-only"
  | "broken"
  | "rejected-at-decode"
  | "rejected-at-compile"
  | "blocked-by-us"
  | "no-runtime-effect";

/**
 * FormFieldOffer is what the editor is allowed to do with a field or an enum
 * member at a given status. `hidden` is not a milder `no`: a `declared-only`
 * field is carried and read by nothing, so *offering* it would promise behaviour
 * that never happens, while a `broken` field must be shown disabled because
 * dropping the value from an imported policy makes that policy BROADER
 * (`concept.md` principle 8, `SC-F505`).
 */
export type FormFieldOffer = "yes" | "no" | "hidden" | "badged";

/**
 * PolicyFormSchemaResponse is the `GET /v1/policy-schema` envelope (`06` §4.9).
 * It wraps the hand-maintained form schema (`SC-F501`) — which is
 * drift-checked in CI against the pinned Sluice source — with the provenance a
 * consumer generating forms needs.
 *
 * **Render `known_gaps` somewhere reachable.** The array ships in the response
 * because the schema is a public artifact and a form generator needs to know
 * what it does not carry: `sluice schema export` cannot emit `RowFilterPolicy`
 * at all, exports zero enums, and makes per-type requiredness invisible. When
 * `SLUICE-U14` lands the array empties and `source` changes; the endpoint does
 * not.
 */
export interface PolicyFormSchemaResponse {
  sluice_version: string;
  form_schema_version: string;
  /** Provenance prose — "hand-maintained (SC-F501); drift-checked in CI …". */
  source: string;
  /** The kind names the schema covers. */
  kinds: PolicyKind[];
  schema: PolicyFormSchema;
  known_gaps: string[];
  /** The `SLUICE-U##` ask that would remove the hand maintenance. */
  upstream: string;
}

/**
 * PolicyFormSchema is the schema document itself — frozen interface `I3`:
 * **the UI codes against this, not against the Go types.**
 *
 * The field and constraint entries are intentionally left open. They are a
 * large, evolving document owned by `internal/policymodel/form-schema.json`, and
 * a partial TypeScript mirror would be a second source of truth that drifts from
 * it silently and re-offers fields the pinned build does not read. Editors
 * narrow what they read, in one place each.
 */
export interface PolicyFormSchema {
  formSchemaVersion: string;
  /** The Sluice commit the schema was checked against (`OD-01`). */
  sluiceCommit: string;
  knownGaps: string[];
  statuses: Array<{
    status: FormFieldStatus;
    badge: string;
    help: string;
    offer: FormFieldOffer;
  }>;
  enums: Record<string, unknown>;
  groups: Record<string, unknown>;
  kinds: PolicyKindSchema[];
  /** The two CEL environments the pinned build constructs, and the variables each
   * one declares (`SC-F510`). It is here rather than hard-coded in the editor for
   * the reason every other vocabulary is: the linter enforces this list, so the
   * editor has to OFFER this list or the two disagree. */
  celVocabulary: CelEnvironmentDoc[];
}

/** CelEnvironmentDoc is one CEL environment as the schema describes it. */
export interface CelEnvironmentDoc {
  /** `condition` or `row-filter`. The same expression can be legal in one and
   * refused in the other, so the environment is never defaulted. */
  environment: CelEnvironmentName;
  title: string;
  /** The field paths this environment applies to. */
  where: string[];
  source: string;
  /** The contract in one paragraph: what the expression must return, and what
   * happens when it errors at runtime. Render it; do not paraphrase it. */
  help: string;
  variables: CelVariableDoc[];
}

/** CelEnvironmentName is the closed pair. */
export type CelEnvironmentName = "condition" | "row-filter";

/** CelVariableDoc is one declared root variable. */
export interface CelVariableDoc {
  name: string;
  /** Always `map`: every root is declared as `map(string, dyn)`, which is why an
   * unknown field type-checks and then denies at runtime. */
  type: string;
  /** False for a variable the editor must not offer. Exactly one is false —
   * `request`, under `SLUICE-U12` — and `reason` says why. */
  offered: boolean;
  reason?: string;
  upstream?: string;
  /** True for a root whose keys are not enumerable: `row`, whose selections
   * become column references. */
  openMap?: boolean;
  help?: string;
  fields?: CelFieldDoc[];
}

/** CelFieldDoc is one key the activation populates. */
export interface CelFieldDoc {
  name: string;
  /** The runtime shape spelled for a human — `string`, `int`, `bool`,
   * `list of string`, `map`. Documentation only: to the compiler every key is
   * `dyn`, so nothing enforces it. */
  type: string;
  /** True for a field whose own keys are not enumerable — `subject.claims`,
   * `request.headers`. */
  openMap?: boolean;
  help?: string;
}

/**
 * CelFinding is one diagnostic from `POST /v1/policy-cel-check` (`SC-F510`).
 *
 * It is a [PolicyFinding] plus two byte offsets into the expression that was sent,
 * because a CEL expression is the one authored value with an INSIDE: every other
 * finding anchors to a field path, and a field path cannot say which of two
 * operands is wrong. The offsets are what `bn-code` draws its inline diagnostic
 * from.
 */
export interface CelFinding extends PolicyFinding {
  from: number;
  to: number;
}

/** CelCheckResult is the check's answer. */
export interface CelCheckResult {
  findings: CelFinding[];
  blocks_save: boolean;
  blocks_publish: boolean;
  /** Returned even when nothing fired, and worth rendering: a clean answer means
   * no rule matched, which is not the same as one that compiles. */
  basis: string;
}

/**
 * checkCelExpression runs the control-plane CEL subset check (`SC-F510`).
 *
 * It takes no instance because the subset is a property of the pinned binary and
 * not of a deployment, and it is the AUTHORITY for the editor's inline
 * diagnostics: there is deliberately no second implementation of the subset in
 * this bundle, because two implementations of "is this expression in the subset"
 * would agree until a pin bump moved one of them.
 */
export function checkCelExpression(
  expression: string,
  environment: CelEnvironmentName,
): Promise<CelCheckResult> {
  return sendJSON<CelCheckResult>("/v1/policy-cel-check", "POST", { expression, environment });
}

/** PolicyKindSchema is one kind's form definition, inside [PolicyFormSchema]. */
export interface PolicyKindSchema {
  kind: PolicyKind;
  apiVersion: string;
  title: string;
  /** The `SC-F###` that owns this kind's editor. */
  feature: string;
  /** True when the kind carries `spec.enforcementMode`. */
  enforcement: boolean;
  /** True when any change to this kind is restart-class (`SC-F409`). */
  restart: boolean;
  /** `whole-query` or `per-table` — the scope of this kind's `exclude`, which is
   * the difference between "excluding one table lifts protection from the others in
   * a join" and "it does not". The editor states the kind's own scope rather than a
   * generic sentence (`SC-F509`). */
  excludeScope?: "whole-query" | "per-table";
  help: string;
  fields: unknown[];
  constraints: Array<{
    id: string;
    severity: FindingSeverity;
    fields: string[];
    statement: string;
    enforcedBy: string;
    feature?: string;
    linter?: string;
  }>;
}

/**
 * getPolicyFormSchema fetches the form schema, optionally narrowed to one kind.
 * Cache it for the session: it is large, static for the life of the deployment,
 * and every editor needs it.
 *
 * **It normalizes two wire shapes onto one contract, and that is not defensive
 * padding.** `06` §4.9 specifies the envelope this function returns —
 * `{sluice_version, form_schema_version, source, schema, known_gaps, upstream}` —
 * while the handler that serves the route today writes `policymodel`'s embedded
 * bytes VERBATIM (`internal/policies/routes.go`, `api.schema`), so the response is
 * the schema *document* with no envelope around it. A client coding against the
 * documented shape reads `response.schema` as `undefined` and every editor renders
 * an empty form.
 *
 * Rather than push that discrepancy into fourteen editors, the typed client is the
 * one place that reconciles it: a body carrying `schema` is the envelope, and a body
 * carrying `formSchemaVersion` at its top level is the bare document, which is
 * wrapped here. Delete the second branch when the handler grows the envelope; the
 * consumers do not change either way.
 */
export async function getPolicyFormSchema(kind?: PolicyKind): Promise<PolicyFormSchemaResponse> {
  const body = await getJSON<Partial<PolicyFormSchemaResponse> & Partial<PolicyFormSchema>>(
    `/v1/policy-schema${query({ kind })}`,
  );
  if (body.schema && typeof body.schema === "object") return body as PolicyFormSchemaResponse;
  const doc = body as PolicyFormSchema;
  return {
    sluice_version: doc.sluiceCommit ?? "",
    form_schema_version: doc.formSchemaVersion ?? "",
    source: "hand-maintained (SC-F501); drift-checked in CI against the pinned Sluice source",
    kinds: (doc.kinds ?? []).map((k) => k.kind),
    schema: doc,
    known_gaps: doc.knownGaps ?? [],
    upstream: "SLUICE-U14",
  };
}

// ───────────────────────────── the effective access review report (SC-F825) ──
//
// A compliance artifact, and the one surface in the product whose value is entirely
// in what it refuses to claim. It describes the AUTHORED configuration: for every
// identity the bindings can resolve and every table the schema cache knows, which
// policy objects can reach that pair and what they say.
//
// **It is never a decision.** Sluice Cloud runs no policy engine, so a cell carries a
// posture rather than a verdict, and where an object's effect depends on something
// only the gateway can evaluate the cell says so. Render `basis` and `limits`
// verbatim: a permission matrix shown without them is the artifact this feature
// exists to replace.

/** ReviewPosture is what a cell says about a (principal, table) pair. None of the
 * five is a verdict on a query. */
export type ReviewPosture =
  | "denied-by-default"
  | "denied-explicitly"
  | "allow-authored"
  | "conditional"
  | "indeterminate";

/** ReviewCertainty is how much of a match the report resolved statically. */
export type ReviewCertainty = "static" | "conditional" | "unknown";

/** ReviewPrincipal is one row: an identity the authored bindings can resolve.
 *
 * `enumerable` false is a JWT issuer, whose row stands for however many people hold a
 * token from it — the subject id and the groups arrive as claims at request time, so
 * the report knows the issuer is trusted and nothing about who holds a token. */
export interface ReviewPrincipal {
  binding: string;
  /** `api-key` or `jwt-issuer`. */
  kind: string;
  id?: string;
  issuer?: string;
  groups?: string[];
  enumerable: boolean;
  note?: string;
}

/** ReviewMatch is one authored object that can reach a cell.
 *
 * `shadow` is the field a reader must not miss: an `Audit` or `DryRun` object records
 * what it would have applied and lets the query run, so it never contributes to a
 * posture. Counting an audit-mode deny as protection would be the most dangerous
 * possible misreading of this report. */
export interface ReviewMatch {
  kind: string;
  name: string;
  priority: number;
  enforcement: string;
  shadow?: boolean;
  /** `allow`, `deny`, `row-filter`, `column-mask`, `reject`, `approval`. */
  effect: string;
  certainty: ReviewCertainty;
  why?: string;
}

/** ReviewMaskedColumn is one column a mask can cover, with `03` §7.4's class. The
 * classes are an ORDERING and not a total order — comparing `partial(1,0)` with
 * `truncate(3)` is not meaningful — so the report ranks by class and the UI shows the
 * label rather than expecting the reader to know the table. */
export interface ReviewMaskedColumn {
  column: string;
  policy: string;
  mask: string;
  class: number;
  class_label: string;
  shadow?: boolean;
  certainty: ReviewCertainty;
}

/** ReviewCell is one (principal, table) pair. `principal` indexes
 * `AccessReview.principals`. */
export interface ReviewCell {
  principal: number;
  table: string;
  posture: ReviewPosture;
  matches?: ReviewMatch[];
  row_filters?: string[];
  /** At least one matching row filter uses a template, which resolves per request and
   * DENIES the query when it cannot resolve — so its effect is unknowable in both
   * directions. */
  templated?: boolean;
  masked?: ReviewMaskedColumn[];
  notes?: string[];
}

/** ReviewTable is one column of the matrix. */
export interface ReviewTable {
  catalog: string;
  schema: string;
  table: string;
  columns?: string[];
}

/** ReviewSchemaStatus is the introspection cache's freshness, on the artifact
 * because limit 2 of the report is precisely about it. */
export interface ReviewSchemaStatus {
  tables: ReviewTable[];
  fetched_at?: string;
  fresh: boolean;
  degraded: boolean;
  truncated?: boolean;
  warning?: string;
}

/** AccessReview is `GET /v1/instances/{id}/access-review`. */
export interface AccessReview {
  instance: string;
  organization: string;
  generation: number;
  generation_state: string;
  generated_at: string;
  principals: ReviewPrincipal[];
  tables: ReviewTable[];
  /** Dense: every (principal, table) pair appears, so "no policy reaches this pair"
   * is distinguishable from "this pair was not examined". */
  cells: ReviewCell[];
  summary: Partial<Record<ReviewPosture, number>>;
  schema: ReviewSchemaStatus;
  /** The paragraph that says what this report is. Show it. */
  basis: string;
  /** The five constraints, as sentences. Show them. */
  limits: string[];
  warnings?: string[];
  truncated?: boolean;
}

/** AccessReviewParams narrows the matrix. The three resource filters match by EXACT
 * name, never by pattern: resolving a wildcard would be a second partial
 * implementation of Sluice's matcher in the one artifact that must not contain one. */
export interface AccessReviewParams {
  catalog?: string;
  schema?: string;
  table?: string;
  binding?: string;
}

/** getAccessReview reads the report. */
export function getAccessReview(
  instanceId: string,
  params: AccessReviewParams = {},
): Promise<AccessReview> {
  return getJSON<AccessReview>(
    `/v1/instances/${encodeURIComponent(instanceId)}/access-review${query({ ...params })}`,
  );
}

/** accessReviewCSVHref is the download URL for the keepable artifact: one row per
 * cell, with the basis and the limits as comment lines above the header, readable
 * three years later without this API. */
export function accessReviewCSVHref(instanceId: string, params: AccessReviewParams = {}): string {
  return `/v1/instances/${encodeURIComponent(instanceId)}/access-review${query({
    ...params,
    format: "csv",
  })}`;
}

// ────────────────────────────────────────── SC-F528 outcome preview ──

/**
 * PreviewSubject is the subject shape an outcome preview is computed for
 * (`SC-F528`).
 *
 * The fields are `policy explain`'s own, so a shape that produced a preview can be
 * handed to the authoritative explain with no re-typing. `claims` are ECHOED and
 * never compared: the six claim operators are unvalidated upstream, so comparing
 * them in the control plane would put a second implementation of a security
 * boundary there.
 */
export interface PreviewSubject {
  user: string;
  issuer?: string;
  email?: string;
  groups?: string[];
  claims?: Record<string, string>;
  /** `jwt` (the default, and what both proxied commands assume) or `api_key` —
   * the shape `policy explain` cannot construct, which the answer then marks as
   * unverifiable (`SLUICE-U27`). */
  auth_method?: "jwt" | "api_key";
}

/** PreviewRequest is the body of `POST /v1/instances/{id}/outcome-preview`. */
export interface PreviewRequest {
  /** `draft` (the default, falling back to `active`) or `active`. An authoring
   * preview defaults to what you are writing; the answer says which it read. */
  target?: "draft" | "active";
  subject: PreviewSubject;
  /** Fully-qualified `catalog.schema.table` targets. Empty means every table the
   * schema cache knows, capped server-side. */
  tables?: string[];
  action?: string;
}

/** PreviewStage is one link of Sluice's fixed, non-configurable precedence chain. */
export type PreviewStage =
  | "access"
  | "row-filter"
  | "column-mask"
  | "rewrite"
  | "reject"
  | "approval";

/** PreviewStep is one stage's answer for one table. */
export interface PreviewStep {
  stage: PreviewStage;
  title: string;
  statement: string;
  certainty: ReviewCertainty;
  why?: string;
  matches?: ReviewMatch[];
  /** True for a stage the chain never reaches — the short-circuit an explicit deny
   * causes. "This stage did nothing" and "this stage never ran" have different
   * fixes, so the two are distinguishable. */
  skipped?: boolean;
}

/** PreviewFoldMember is one row filter's position in the fold for one table. */
export interface PreviewFoldMember {
  name: string;
  priority: number;
  combine: string;
  /** 1-based in fold order: priority descending, then name ascending. */
  position: number;
  installs: boolean;
  /** True for a permissive attachment that is not the first: it ORs, so rows an
   * earlier restrictive filter hid become visible again. */
  widens?: boolean;
  shadow?: boolean;
  templated?: boolean;
  expression?: boolean;
}

/** PreviewFold is the folded row filter for one table. */
export interface PreviewFold {
  /** The fold over POLICY NAMES — `(tenant) OR vip` — with the same left-nesting
   * the engine's own `combinePredicates` produces. */
  expression: string;
  members: PreviewFoldMember[];
  templated?: boolean;
  certainty: ReviewCertainty;
  why?: string;
}

/** PreviewMask is one column's mask resolution: the winner and what it suppressed. */
export interface PreviewMask {
  column: string;
  winner: ReviewMaskedColumn;
  suppressed?: ReviewMaskedColumn[];
  /** Names the tie-break that decided it, and says so in disclosure terms when the
   * winner reveals MORE than a candidate it suppressed. */
  note?: string;
}

/** PreviewRewrite is the folded rewrite effect. Limits and timeouts fold
 * restrictively — smallest non-zero wins — while a sample is first-write-wins. */
export interface PreviewRewrite {
  limit_max?: number;
  timeout?: string;
  sample?: string;
  policies?: string[];
}

/** PreviewTable is the whole chain for one table. */
export interface PreviewTable {
  table: string;
  /** `SC-F825`'s vocabulary, reused so the product has one word for each answer. */
  posture: ReviewPosture;
  steps: PreviewStep[];
  fold?: PreviewFold;
  masks?: PreviewMask[];
  rewrite?: PreviewRewrite;
  notes?: string[];
}

/** IndeterminacyCause is the closed vocabulary of reasons the preview cannot
 * decide something. Closed so a client can group by it and a reader learns a
 * fixed, small set of gaps. */
export type IndeterminacyCause =
  | "cel-condition"
  | "claim-comparison"
  | "api-key-subject"
  | "reject-rule"
  | "row-filter-template"
  | "row-filter-expression"
  | "sql-action"
  | "unknown-tag"
  | "undecodable-object"
  | "schema-cache"
  | "approval";

/** Indeterminacy is one named gap in the answer. */
export interface Indeterminacy {
  cause: IndeterminacyCause;
  table?: string;
  policy?: string;
  detail: string;
  /** The `SLUICE-U##` ask that would close it, for the gaps that have one. Empty
   * means the gap is a design boundary: the control plane is not going to start
   * evaluating CEL. */
  upstream?: string;
}

/** PreviewVerification says whether this answer's shape is one the golden-fixture
 * drift test against the pinned binary covers. */
export interface PreviewVerification {
  drift_tested: boolean;
  sluice_commit: string;
  statement: string;
}

/** OutcomePreview is `POST /v1/instances/{id}/outcome-preview` (`SC-F528`). */
export interface OutcomePreview {
  instance: string;
  organization: string;
  generation: number;
  generation_state: string;
  generated_at: string;
  subject: PreviewSubject;
  action: string;
  tables: PreviewTable[];
  /** `03` §3.14 obligation 1. Render it in the panel header, not in a tooltip. */
  advisory: string;
  /** Obligation 2: this gates nothing, and the sentence names what does. */
  never_gates: string;
  /** Obligation 3's status for this particular answer. */
  verified: PreviewVerification;
  indeterminacies: Indeterminacy[];
  limits: string[];
  warnings?: string[];
  truncated?: boolean;
}

/** getOutcomePreview computes the advisory preview (`SC-F528`). */
export function getOutcomePreview(
  instanceId: string,
  input: PreviewRequest,
): Promise<OutcomePreview> {
  return sendJSON<OutcomePreview>(
    `/v1/instances/${encodeURIComponent(instanceId)}/outcome-preview`,
    "POST",
    input,
  );
}
